/**
 * PROTOTYPE ONLY — throwaway code, no tests, no error handling, no persistence.
 *
 * Shared vocabulary for the three session-form variants: the draft state they all
 * edit, the mock data they all read, and the field-level atoms (pricing model cards,
 * PWYW rows, status tags) that are the same design in every variant.
 *
 * Deliberately NOT shared: layout. Each variant is free to throw the page shape out.
 */
import {useState} from "react";
import {Alert, Input, InputNumber, Radio, Select, Switch, Tag, Typography} from "antd";

const {Text, Paragraph} = Typography;

/* ------------------------------------------------------------------ state ---- */

/** Mirrors `PricingModel` in `@/features/pricing` — restated so the prototype stands alone. */
export type PricingModel = "free" | "single-fee" | "pwyw";

export type LocationKind = "google-meet" | "phone" | "company-video";

export interface SessionDraft {
  name: string;
  description: string;
  link: string;
  durationMinutes: number;
  pricingModel: PricingModel;
  currency: string;
  /** The fixed price under Single Fee, the Suggested Price under Pay What You Want. */
  price?: number;
  /** The Minimum Price — Pay What You Want only. */
  minimum?: number;
  scheduleId: string;
  location: LocationKind;
  hideFromProfile: boolean;
  requestPhoneNumber: boolean;
  redirectAfterPurchase: boolean;
  redirectUrl: string;
}

/** The three things section 3 of the doc offers after creation. */
export interface Customisation {
  coverImage: boolean;
  checkoutQuestions: number;
  automations: boolean;
}

export const EMPTY_DRAFT: SessionDraft = {
  name: "",
  description: "",
  link: "",
  durationMinutes: 30,
  pricingModel: "free",
  currency: "USD",
  scheduleId: "working-hours",
  location: "google-meet",
  hideFromProfile: false,
  requestPhoneNumber: false,
  redirectAfterPurchase: false,
  redirectUrl: "",
};

export const EMPTY_CUSTOMISATION: Customisation = {
  coverImage: false,
  checkoutQuestions: 0,
  automations: false,
};

export type DraftSetter = <K extends keyof SessionDraft>(
  key: K,
  value: SessionDraft[K]
) => void;

/** One in-memory store, shared by every screen of a clickthrough. No persistence. */
export function useSessionDraft() {
  const [draft, setDraft] = useState<SessionDraft>(EMPTY_DRAFT);
  const [customisation, setCustomisation] = useState<Customisation>(EMPTY_CUSTOMISATION);

  const set: DraftSetter = (key, value) => {
    setDraft((current) => ({...current, [key]: value}));
  };

  return {draft, set, customisation, setCustomisation};
}

/* -------------------------------------------------------------- mock data ---- */

/** Stubbed: the prototype answers "what should this look like", not "does the API work". */
export const GOOGLE_CALENDAR_CONNECTED = false;

export const SCHEDULES = [
  {
    id: "working-hours",
    name: "Working hours",
    days: "Monday to Friday",
    time: "9:00 AM - 5:00 PM",
  },
  {
    id: "evenings",
    name: "Evenings",
    days: "Tuesday and Thursday",
    time: "6:00 PM - 9:00 PM",
  },
  {
    id: "weekends",
    name: "Weekends",
    days: "Saturday and Sunday",
    time: "10:00 AM - 2:00 PM",
  },
];

export const CURRENCIES = ["USD", "GBP", "EUR", "NGN", "KES", "ZAR"];

/** Flat 10 in every currency, matching PLATFORM_MINIMUM_BY_CURRENCY. */
export const PLATFORM_MINIMUM = 10;

export const DURATIONS = [15, 30, 45, 60, 90];

export const LOCATION_LABELS: Record<LocationKind, string> = {
  "google-meet": "Google Meet",
  phone: "Phone call",
  "company-video": "Coachli video call",
};

/* ------------------------------------------------------------------ atoms ---- */

/**
 * Section 4 of the doc: Required blocks progression, Recommended does not but the
 * customer notices, Optional is a nice-to-have.
 */
export type Importance = "required" | "recommended" | "optional";

export function ImportanceTag({level}: {level: Importance}) {
  if (level === "required") {
    return <Tag color="red">Required</Tag>;
  }
  if (level === "recommended") {
    return <Tag color="blue">Recommended</Tag>;
  }
  return <Tag>Optional</Tag>;
}

export function priceSummary(draft: SessionDraft): string {
  if (draft.pricingModel === "free") {
    return "Free";
  }
  if (draft.pricingModel === "single-fee") {
    return draft.currency + " " + (draft.price ?? 0);
  }
  const suggested = draft.price
    ? " · suggested " + draft.currency + " " + draft.price
    : "";
  return (
    "Pay what you want · from " + draft.currency + " " + (draft.minimum ?? 0) + suggested
  );
}

export function scheduleSummary(draft: SessionDraft): string {
  const schedule = SCHEDULES.find((option) => option.id === draft.scheduleId);
  return schedule ? schedule.days + " · " + schedule.time : "Not selected";
}

/* ------------------------------------------------------------ field groups ---- */

interface FieldsProps {
  draft: SessionDraft;
  set: DraftSetter;
}

export function GeneralFields({draft, set}: FieldsProps) {
  return (
    <div style={{display: "grid", gap: 16}}>
      <label>
        <Text strong>Session name</Text>
        <Input
          value={draft.name}
          placeholder="Career coaching"
          onChange={(event) => set("name", event.target.value)}
        />
      </label>
      <label>
        <Text strong>Description</Text>
        <Input.TextArea
          rows={3}
          value={draft.description}
          placeholder="What will customers get out of this session?"
          onChange={(event) => set("description", event.target.value)}
        />
      </label>
      <label>
        <Text strong>Session link</Text>
        <Input
          addonBefore="coachli.co/you/"
          value={draft.link}
          placeholder="career-coaching"
          onChange={(event) => set("link", event.target.value)}
        />
      </label>
      <label>
        <Text strong>Duration</Text>
        <Select
          style={{width: "100%"}}
          value={draft.durationMinutes}
          onChange={(value) => set("durationMinutes", value)}
          options={DURATIONS.map((minutes) => ({
            value: minutes,
            label: minutes + " minutes",
          }))}
        />
      </label>
    </div>
  );
}

/**
 * The three Pricing Models, mirroring PricingModelCardsFormField's copy. Pay What You
 * Want adds a Minimum Price and a Suggested Price; the Minimum carries the currency,
 * as it does in the real form.
 */
export function PricingFields({draft, set}: FieldsProps) {
  return (
    <div style={{display: "grid", gap: 16}}>
      <Radio.Group
        value={draft.pricingModel}
        onChange={(event) => set("pricingModel", event.target.value)}
        style={{display: "grid", gap: 8}}
      >
        <PricingCard
          value="free"
          title="Free"
          description="Give customers free access"
          selected={draft.pricingModel === "free"}
        />
        <PricingCard
          value="single-fee"
          title="Single fee"
          description="Charge a one-time fee"
          selected={draft.pricingModel === "single-fee"}
        />
        <PricingCard
          value="pwyw"
          title="Pay what you want (PWYW)"
          description="Allow customers to name a price"
          selected={draft.pricingModel === "pwyw"}
        />
      </Radio.Group>

      {draft.pricingModel !== "free" ? (
        <div style={{display: "grid", gap: 12}}>
          <label>
            <Text strong>Currency</Text>
            <Select
              style={{width: "100%"}}
              value={draft.currency}
              onChange={(value) => set("currency", value)}
              options={CURRENCIES.map((code) => ({value: code, label: code}))}
            />
          </label>

          {draft.pricingModel === "single-fee" ? (
            <label>
              <Text strong>Price</Text>
              <InputNumber
                style={{width: "100%"}}
                min={0}
                value={draft.price}
                onChange={(value) => set("price", value ?? undefined)}
              />
            </label>
          ) : (
            <>
              <label>
                <Text strong>Minimum price</Text>
                <InputNumber
                  style={{width: "100%"}}
                  min={0}
                  value={draft.minimum}
                  onChange={(value) => set("minimum", value ?? undefined)}
                />
                <Text type="secondary" style={{fontSize: 12}}>
                  The least a customer may pay. Platform minimum is {draft.currency}{" "}
                  {PLATFORM_MINIMUM}.
                </Text>
              </label>
              <label>
                <Text strong>Suggested price</Text>
                <InputNumber
                  style={{width: "100%"}}
                  min={0}
                  value={draft.price}
                  onChange={(value) => set("price", value ?? undefined)}
                />
                <Text type="secondary" style={{fontSize: 12}}>
                  What the checkout pre-fills. Customers can pay more or less, down to the
                  minimum.
                </Text>
              </label>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PricingCard({
  value,
  title,
  description,
  selected,
}: {
  value: PricingModel;
  title: string;
  description: string;
  selected: boolean;
}) {
  return (
    <Radio
      value={value}
      style={{
        display: "block",
        padding: 12,
        border: "1px solid " + (selected ? "#1677ff" : "#e5e7eb"),
        borderRadius: 8,
        background: selected ? "#f0f7ff" : "#fff",
      }}
    >
      <Text strong>{title}</Text>
      <br />
      <Text type="secondary" style={{fontSize: 12}}>
        {description}
      </Text>
    </Radio>
  );
}

export function AvailabilityFields({draft, set}: FieldsProps) {
  const schedule = SCHEDULES.find((option) => option.id === draft.scheduleId);

  return (
    <div style={{display: "grid", gap: 16}}>
      <label>
        <Text strong>Schedule</Text>
        <Select
          style={{width: "100%"}}
          value={draft.scheduleId}
          onChange={(value) => set("scheduleId", value)}
          options={SCHEDULES.map((option) => ({value: option.id, label: option.name}))}
        />
      </label>
      {schedule ? (
        <Alert
          type="info"
          showIcon
          message={schedule.days + " · " + schedule.time}
          description="Customers can only book inside these hours."
        />
      ) : null}
    </div>
  );
}

export function LocationFields({draft, set}: FieldsProps) {
  return (
    <div style={{display: "grid", gap: 16}}>
      <Radio.Group
        value={draft.location}
        onChange={(event) => set("location", event.target.value)}
        style={{display: "grid", gap: 8}}
      >
        <Radio value="google-meet">Google Meet</Radio>
        <Radio value="phone">Phone call</Radio>
        <Radio value="company-video">Coachli video call</Radio>
      </Radio.Group>

      {draft.location === "google-meet" && !GOOGLE_CALENDAR_CONNECTED ? (
        <Alert
          type="warning"
          showIcon
          message="Google Calendar is not connected"
          description="Connect it so Coachli can create the Meet link and hold the slot in your calendar."
          action={<a href="#connect-calendar">Connect calendar</a>}
        />
      ) : null}
    </div>
  );
}

export function SettingsFields({draft, set}: FieldsProps) {
  return (
    <div style={{display: "grid", gap: 16}}>
      <ToggleRow
        label="Hide from profile"
        hint="The session stays bookable by direct link only."
        checked={draft.hideFromProfile}
        onChange={(checked) => set("hideFromProfile", checked)}
      />
      <ToggleRow
        label="Request customer phone number"
        hint="Asked at checkout."
        checked={draft.requestPhoneNumber}
        onChange={(checked) => set("requestPhoneNumber", checked)}
      />
      <ToggleRow
        label="Redirect customer after purchase"
        checked={draft.redirectAfterPurchase}
        onChange={(checked) => set("redirectAfterPurchase", checked)}
      />
      {draft.redirectAfterPurchase ? (
        <label>
          <Text strong>Redirect URL</Text>
          <Input
            value={draft.redirectUrl}
            placeholder="https://"
            onChange={(event) => set("redirectUrl", event.target.value)}
          />
        </label>
      ) : null}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div style={{display: "flex", justifyContent: "space-between", gap: 16}}>
      <div>
        <Text strong>{label}</Text>
        {hint ? (
          <Paragraph type="secondary" style={{fontSize: 12, marginBottom: 0}}>
            {hint}
          </Paragraph>
        ) : null}
      </div>
      <Switch checked={checked} onChange={onChange} />
    </div>
  );
}

/* --------------------------------------------------------------- sections ---- */

export type SectionKey = "general" | "pricing" | "availability" | "location" | "settings";

export const SECTIONS: {key: SectionKey; title: string; blurb: string}[] = [
  {
    key: "general",
    title: "General information",
    blurb: "Name, description, duration and session link",
  },
  {key: "pricing", title: "Pricing", blurb: "How customers pay for this session"},
  {key: "availability", title: "Availability", blurb: "When customers can book"},
  {key: "location", title: "Location", blurb: "Where the session happens"},
  {
    key: "settings",
    title: "Settings",
    blurb: "Profile visibility, customer information and redirect settings",
  },
];

export function SectionFields({
  section,
  draft,
  set,
}: {section: SectionKey} & FieldsProps) {
  switch (section) {
    case "general":
      return <GeneralFields draft={draft} set={set} />;
    case "pricing":
      return <PricingFields draft={draft} set={set} />;
    case "availability":
      return <AvailabilityFields draft={draft} set={set} />;
    case "location":
      return <LocationFields draft={draft} set={set} />;
    case "settings":
      return <SettingsFields draft={draft} set={set} />;
    default:
      return null;
  }
}

/** One line per section, used by every variant's summary — the content, not the frame. */
export function sectionSummary(section: SectionKey, draft: SessionDraft): string[] {
  switch (section) {
    case "general":
      return [
        "Name: " + (draft.name || "—"),
        "Duration: " + draft.durationMinutes + " minutes",
        "Description: " + (draft.description || "—"),
      ];
    case "pricing":
      return [priceSummary(draft)];
    case "availability":
      return [scheduleSummary(draft)];
    case "location":
      return [LOCATION_LABELS[draft.location]];
    case "settings":
      return [
        "Hide from profile: " + (draft.hideFromProfile ? "Yes" : "No"),
        "Request phone number: " + (draft.requestPhoneNumber ? "Yes" : "No"),
        "Redirect after purchase: " + (draft.redirectAfterPurchase ? "Yes" : "No"),
      ];
    default:
      return [];
  }
}

/** Summary of the section, one line per section, for the manage/task-list screens. */
export function sectionOneLiner(section: SectionKey, draft: SessionDraft): string {
  switch (section) {
    case "general":
      return draft.name || "Not set";
    case "pricing":
      return priceSummary(draft);
    case "availability":
      return scheduleSummary(draft);
    case "location":
      return LOCATION_LABELS[draft.location];
    case "settings":
      return draft.hideFromProfile ? "Hidden from profile" : "Visible on profile";
    default:
      return "";
  }
}

/** The three booking-experience items, with the importance the doc assigns each. */
export function customisationItems(customisation: Customisation): {
  key: keyof Customisation;
  title: string;
  level: Importance;
  status: string;
  blurb: string;
}[] {
  return [
    {
      key: "coverImage",
      title: "Cover image",
      level: "recommended",
      status: customisation.coverImage ? "Custom image added" : "No custom image",
      blurb:
        "Add a custom image so customers can recognise your session. Without one, customers see the default image.",
    },
    {
      key: "checkoutQuestions",
      title: "Checkout questions",
      level: "optional",
      status: customisation.checkoutQuestions
        ? customisation.checkoutQuestions + " questions configured"
        : "None configured",
      blurb: "Collect extra information from customers when they book.",
    },
    {
      key: "automations",
      title: "Automations",
      level: "optional",
      status: customisation.automations ? "Configured" : "Not configured",
      blurb: "Set up automated emails and actions for your bookings.",
    },
  ];
}
